/**
 * <av-sticky-atc> — mobile sticky Add to Bag bar for the product page.
 * Shows once the real price/buy-buttons area scrolls out of view above the
 * viewport, and mirrors price/availability from Dawn's native
 * `variant-change` pub/sub event instead of reformatting money in JS.
 */
if (!customElements.get('av-sticky-atc')) {
  class AvocadoStickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.priceEl = this.querySelector('[data-av-sticky-price]');
      this.buttonEl = this.querySelector('[data-av-sticky-button]');
      this.selectionEl = this.querySelector('[data-av-sticky-selection]');
      this.onInputFocus = () => {
        const active = document.activeElement;
        const editable = active?.matches('textarea, [contenteditable="true"], input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"])');
        this.toggleAttribute('data-input-focused', !!editable);
      };
      document.addEventListener('focusin', this.onInputFocus);
      document.addEventListener('focusout', this.onInputFocus);
      this.sourceButton = document.getElementById(this.dataset.sourceButton);
      this.buttonObserver = new MutationObserver(() => this.syncButton());
      if (this.sourceButton) {
        this.buttonObserver.observe(this.sourceButton, {
          attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true, subtree: true, characterData: true,
        });
        this.syncButton();
      }

      this.observeTarget = document.getElementById(this.dataset.observe);
      if (this.observeTarget) {
        this.onScroll = () => {
          if (this.visibilityFrame) return;
          this.visibilityFrame = requestAnimationFrame(() => {
            this.visibilityFrame = null;
            this.syncVisibility();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.syncVisibility();
      }

      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data.sectionId !== this.sectionId) return;

        const newPrice = event.data.html.getElementById(`price-${this.sectionId}`);
        if (newPrice && this.priceEl) {
          this.priceEl.innerHTML = newPrice.innerHTML;
        }
        const picker = document.getElementById(`variant-selects-${this.sectionId}`);
        const selectedSize = Array.from(picker?.querySelectorAll('input:checked, select option:checked') || [])
          .find((option) => /size|taille/i.test(option.dataset.optionName || ''));
        if (selectedSize && this.selectionEl) {
          this.selectionEl.textContent = `Size: ${selectedSize.value.replace(" US kids' numeric", '')}`;
        }

        this.syncButton();
        this.syncVisibility();
      });
    }

    syncVisibility() {
      if (this.observeTarget) this.hidden = this.observeTarget.getBoundingClientRect().bottom > 0;
    }

    syncButton() {
      if (!this.sourceButton || !this.buttonEl) return;
      const loading = this.sourceButton.classList.contains('loading');
      this.buttonEl.disabled = this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true';
      this.buttonEl.classList.toggle('loading', loading);
      this.buttonEl.setAttribute('aria-busy', String(loading));
      this.buttonEl.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      const label = this.sourceButton.querySelector('span:first-child');
      if (label) this.buttonEl.querySelector('span:first-child').textContent = label.textContent;
      const productForm = this.sourceButton.closest('product-form');
      if (productForm?.sizeConfirmed === false && this.selectionEl) {
        this.selectionEl.textContent = productForm.dataset.selectSizeLabel;
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.visibilityFrame) cancelAnimationFrame(this.visibilityFrame);
      if (this.unsubscribe) this.unsubscribe();
      this.buttonObserver?.disconnect();
      document.removeEventListener('focusin', this.onInputFocus);
      document.removeEventListener('focusout', this.onInputFocus);
    }
  }

  customElements.define('av-sticky-atc', AvocadoStickyAtc);
}
